/*
 * NOTE: While this page lives in the /profile/ subdirectory, we use
 * the Next.js rewrites and redirects to serve it up on the URL:
 * /interior-designers/<name>-<designerId>
 */

import React from 'react';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { addApolloState, getApollo, ApolloClient } from '@lib/apollo/apolloClient';
import { withAuthenticatedUser } from '@lib/user/auth';
import DesignerProfileAdmin from '@components/pages/DesignerProfile/DesignerProfileAdmin/DesignerProfile';
import DesignerProfile from '@components/pages/DesignerProfile/DesignerProfile/DesignerProfile';
import PROFILE_QUERY, {
  IDesignerProfile,
  IDesignerProfileData,
  IDesignerProfileVariables
}
  from '@components/pages/DesignerProfile/graphql/designer-profile-query.graphql';
import BOARD_QUERY, {
  IDesignerBoard,
  IDesignerBoardsData,
  IDesignerBoardsVariables
} from '@components/pages/DesignerProfile/graphql/designer-boards.graphql';
import logger from '@lib/logger';
import { HttpStatus } from '@lib/http';
import { lastPathSegment } from '@lib/url';
import Compose from '@components/shared/Compose';
import LoginRegisterModalProvider
  from '@components/shared/providers/LoginRegisterModalProvider/LoginRegisterModalProvider';
import ShareModalProvider from '@components/shared/providers/ShareModalProvider/ShareModalProvider';
import CreateBoardModalProvider
  from '@components/shared/providers/CreateBoardModalProvider/CreateBoardModalProvider';
import AddToBoardModalProvider
  from '@components/shared/providers/AddToBoardModalProvider/AddToBoardModalProvider';
import BoardImagesModalProvider from '@components/shared/providers/BoardImagesModalProvider/BoardImagesModalProvider';
import { IAuthResponse } from '@models/shared/i-auth-response';
import { notFound } from '@utils/navigation/serverSideHelpers';

interface IDesignerProfilePageProps {
  designer: IDesignerProfile;
  initialBoards: IDesignerBoard[];
  user?: IAuthResponse | null;
}
export default function DesignerProfilePage(props: IDesignerProfilePageProps) {
  const userRole = props.user?.role;
  return (
    <>
      <Head>
        <script async src="https://www.youtube.com/iframe_api" />
      </Head>
      <Compose
        components={[
          ShareModalProvider,
          LoginRegisterModalProvider,
          CreateBoardModalProvider,
          AddToBoardModalProvider,
          BoardImagesModalProvider,
        ]}
      >
        {
          userRole === 'admin' || userRole === 'designer'
            ? <DesignerProfileAdmin {...props} />
            : <DesignerProfile {...props} />
        }
      </Compose>
    </>
  );
}

export const getServerSideProps = withAuthenticatedUser(async ({
  user,
}, ctx: GetServerSidePropsContext) => {
  const log = logger('DesignerProfile');

  const { query: { designer_slug: slug } } = ctx;

  const designerSlug = Array.isArray(slug) ? slug[0] : slug;

  const designerId = getDesignerIdFromSlug(designerSlug);

  if (!designerId) {
    return notFound;
  }

  const apolloClient = getApollo({
    jwt: user?.jwt,
    cacheEnabled: true,
  });

  try {
    const [profileResponse, boardsResponse] = await Promise.all([
      getDesignerProfile(apolloClient, designerId),
      getDesignerBoards(apolloClient, designerId)
    ]);

    const designer = profileResponse.data?.designerById;
    if (designer) {
      if (designer.active !== 1 || designer.isHidden) {
        return {
          redirect: {
            destination: '/interior-designers/',
            statusCode: HttpStatus.PERMANENT_REDIRECT
          }
        };
      }

      if (lastPathSegment(designer.profileUri) !== designerSlug) {
        return {
          redirect: {
            destination: designer.profileUri,
            statusCode: HttpStatus.PERMANENT_REDIRECT
          }
        };
      }

      return addApolloState(apolloClient,
        {
          props: {
            designer,
            initialBoards: boardsResponse.data?.designerPublicBoards.items || [],
            user,
          }
        });
    }
  } catch (e) {
    log.error({ designerId, err: e }, 'Unable to load designer profile');
  }

  return {
    notFound: true
  };
}, {
  requireAuth: false,
  includeExperimentVariations: true
});

function getDesignerIdFromSlug(slug: string | undefined) {
  if (!slug) {
    return undefined;
  }

  const match = slug.match(/(?:.*-)?(\d+)$/);
  if (!match) {
    return undefined;
  }

  return match[1];
}

async function getDesignerProfile<T>(
  apolloClient: ApolloClient<T>,
  designerId: string
) {
  return apolloClient.query<IDesignerProfileData, IDesignerProfileVariables>({
    query: PROFILE_QUERY,
    variables: { designerId }
  });
}

async function getDesignerBoards<T>(
  apolloClient: ApolloClient<T>,
  designerId: string,
) {
  return apolloClient.query<IDesignerBoardsData, IDesignerBoardsVariables>({
    query: BOARD_QUERY,
    variables: {
      designerId,
      pageNumber: 1,
      pageSize: 6,
      includeHidden: false
    }
  });
}
